
import { Vue, Component, Prop } from "vue-property-decorator";
import DropdownInput from "@/components/Reusable/CustomInputs/DropdownInput.vue";
import { configStore } from "@/store/store-accessor";

type InteractionAction = Partial<{
  name: string;
  target: string;
  css_selector: string;
  advanced: Partial<{
    run_only_once: boolean;
    disable_at: string[];
  }>;
}>;

@Component({
  components: { DropdownInput }
})
export default class InteractionsInput extends Vue {
  @Prop()
  value?: Partial<{
    action: string;
    target: string;
    css_selector: string;
    actions: InteractionAction[];
  }>;

  @Prop()
  path!: string;

  cssSelectorCondition = [
    {
      path: "%%CURRENTPATH%%.target",
      operand: "equals",
      value: "custom"
    },
    {
      path: "%%CURRENTPATH%%.trigger",
      operand: "is none of",
      value: ["page_loaded", "page_scrolled"]
    }
  ];

  get fullPath() {
    // ControlsTree adds and extra `interactions` key to the path, this is the only way I found to remove it.
    return this.path.replace(".interactions", "");
  }

  get internalValue() {
    return this.value || { action: "", options: {} };
  }

  beforeCreate() {
    // @ts-ignore
    this.$options.components.ControlsTree =
      require("@/components/Reusable/ControlsTree/ControlsTree.vue").default;
  }

  get actions() {
    return configStore.interactions?.actions || [];
  }

  get triggers() {
    return configStore.interactions?.triggers || [];
  }

  get action() {
    return this.actions.find(action => action.slug === this.value?.action);
  }

  get controls() {
    const actionsControls = this.actions.flatMap(action => {
      return action.controls.map(control => {
        return {
          ...control,
          options: {
            ...control.options,
            condition: {
              path: "%%CURRENTPATH%%.name",
              operand: "equals",
              value: action.slug
            }
          }
        };
      });
    });

    const triggersControls = this.triggers.flatMap(trigger => {
      return trigger.controls.map(control => {
        return {
          ...control,
          options: {
            ...control.options,
            condition: {
              path: "%%CURRENTPATH%%.trigger",
              operand: "equals",
              value: trigger.slug
            }
          }
        };
      });
    });

    return [
      {
        slug: "interactions",
        label: "Interactions",
        noLabel: true,
        options: {
          type: "repeater",
          layout: "vertical",

          repeaterOptions: {
            titleTemplate: "{trigger}",
            defaultTitle: "Interaction",
            buttonName: "Add Interaction",
            noDataMessage: "None"
          }
        },
        children: [
          {
            slug: "trigger",
            label: "Trigger",
            options: {
              layout: "vertical",
              type: "dropdown",

              items: this.triggers.map(trigger => ({
                value: trigger.slug,
                text: trigger.name
              }))
            }
          },
          {
            slug: "target",
            label: "Target",
            options: {
              type: "dropdown",
              layout: "vertical",
              condition: {
                path: "%%CURRENTPATH%%.trigger",
                operand: "is none of",
                value: ["page_loaded", "page_scrolled"]
              },
              items: [
                {
                  value: "this_element",
                  text: "This Element"
                },
                {
                  value: "custom",
                  text: "CSS Selector"
                }
              ]
            }
          },
          {
            slug: "css_selector",
            label: "CSS Selector",
            options: {
              type: "text",
              layout: "vertical",
              condition: [this.cssSelectorCondition]
            }
          },
          {
            slug: "match",
            label: "Match",
            options: {
              layout: "vertical",
              type: "dropdown",
              items: [
                {
                  value: "everywhere",
                  text: "Everywhere"
                },
                {
                  value: "limit_to_this_element",
                  text: "Only Children Of This Element"
                },
                {
                  value: "closest_parent",
                  text: "Closest Parent"
                }
              ],
              placeholder: "Everywhere",
              condition: [this.cssSelectorCondition]
            }
          },
          ...triggersControls,
          {
            slug: "actions",
            label: "Actions",
            options: {
              type: "repeater",
              layout: "vertical",
              repeaterOptions: {
                titleTemplate: "{name}",
                defaultTitle: "Action",
                buttonName: "Add Action"
              }
            },
            children: [
              {
                slug: "name",
                label: "Action",
                options: {
                  layout: "vertical",
                  type: "dropdown",

                  items: this.actions.map(action => ({
                    value: action.slug,
                    text: action.name
                  }))
                }
              },
              ...actionsControls,
              {
                slug: "target",
                label: "Target",
                options: {
                  type: "dropdown",
                  layout: "vertical",

                  items: [
                    {
                      value: "this_element",
                      text: "This Element"
                    },
                    {
                      value: "custom",
                      text: "CSS Selector"
                    },
                    {
                      value: "target",
                      text: "Target"
                    }
                  ]
                }
              },
              {
                slug: "css_selector",
                label: "CSS Selector",
                options: {
                  type: "text",
                  layout: "vertical",
                  condition: [this.cssSelectorCondition]
                }
              },
              {
                slug: "match",
                label: "Match",
                options: {
                  layout: "vertical",
                  type: "dropdown",
                  items: [
                    {
                      value: "everywhere",
                      text: "Everywhere"
                    },
                    {
                      value: "limit_to_this_element",
                      text: "Only Children Of This Element"
                    },
                    {
                      value: "closest_parent",
                      text: "Closest Parent"
                    },
                    {
                      value: "children_of_target",
                      text: "Children of Target"
                    }
                  ],
                  placeholder: "Everywhere",
                  condition: [this.cssSelectorCondition]
                }
              },
              {
                slug: "advanced",
                label: "Advanced",
                keywords: [],
                options: {
                  type: "section",
                  sectionOptions: {
                    type: "popout"
                  }
                },
                children: [
                  {
                    slug: "run_only_once",
                    label: "Run Only Once",
                    options: {
                      type: "toggle",
                      layout: "inline"
                    }
                  },
                  {
                    slug: "disable_at",
                    label: "Disable At",
                    options: {
                      type: "breakpoint_dropdown",
                      layout: "vertical",
                      breakpointOptions: {
                        multiple: true
                      }
                    }
                  }
                ]
              }
            ]
          }
        ]
      }
    ];
  }
}
